import type { ViewMode } from '@mona-roadmap/domain';
import { useCallback, useMemo, useState } from 'react';
import { columnsFor, type ColumnDef, type ColumnKey } from '../lib/columns';
import { applyOrder, clampWidth, emptyPrefs, isCustomized, loadColumnPrefs, moveColumn, saveColumnPrefs, templateFor, type ColumnPrefs } from '../lib/columnLayout';

type PerView = Record<ViewMode, ColumnPrefs>;

export interface ColumnLayout {
  /** The view's columns in the user's order. Stable while a column is being resized (only `template` changes). */
  columns: ColumnDef[];
  /** grid-template-columns for the grid and its header. */
  template: string;
  setWidth: (key: ColumnKey, px: number) => void;
  resetWidth: (key: ColumnKey) => void;
  /** Move `key` to where `target` is. */
  move: (key: ColumnKey, target: ColumnKey) => void;
  customized: boolean;
  reset: () => void;
}

/** Column order and widths per view, persisted in localStorage. */
export function useColumnLayout(view: ViewMode): ColumnLayout {
  const [all, setAll] = useState<PerView>(() => ({ 'by-goal': loadColumnPrefs('by-goal'), 'by-epic': loadColumnPrefs('by-epic') }));
  const prefs = all[view];

  const columns = useMemo(() => applyOrder(columnsFor(view), prefs.order), [view, prefs.order]);
  const template = useMemo(() => templateFor(columns, prefs.widths), [columns, prefs.widths]);

  const update = useCallback(
    (fn: (p: ColumnPrefs) => ColumnPrefs) => {
      setAll((prev) => {
        const next = fn(prev[view]);
        saveColumnPrefs(view, next);
        return { ...prev, [view]: next };
      });
    },
    [view],
  );

  const setWidth = useCallback((key: ColumnKey, px: number) => update((p) => ({ ...p, widths: { ...p.widths, [key]: clampWidth(px) } })), [update]);
  const resetWidth = useCallback(
    (key: ColumnKey) =>
      update((p) => {
        const widths = { ...p.widths };
        delete widths[key];
        return { ...p, widths };
      }),
    [update],
  );
  const move = useCallback(
    (key: ColumnKey, target: ColumnKey) => update((p) => ({ ...p, order: moveColumn(applyOrder(columnsFor(view), p.order).map((c) => c.key), key, target) })),
    [update, view],
  );
  const reset = useCallback(() => update(() => emptyPrefs()), [update]);

  return { columns, template, setWidth, resetWidth, move, customized: isCustomized(prefs), reset };
}
